// src/lib/auth.js
import { supabase } from "./supabase.js";

/**
 * Log in with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{ success: boolean, role?: string, message?: string }>}
 */
export async function login(email, password) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error("Login failed:", error);
      return { success: false, message: "Invalid email or password" };
    }

    const role = data.user?.user_metadata?.role;
    if (!role) {
      console.error("User has no role in metadata:", data.user);
      return { success: false, message: "User role not found. Contact admin." };
    }

    localStorage.setItem("proofride_role", role);
    return { success: true, role };
  } catch (err) {
    console.error("Login exception:", err);
    return { success: false, message: "Login failed. Please try again." };
  }
}

/**
 * Log out current user
 * @returns {Promise<{ success: boolean }>}
 */
export async function logout() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Logout failed:", error);
      return { success: false };
    }

    localStorage.removeItem("proofride_role");
    return { success: true };
  } catch (err) {
    console.error("Logout exception:", err);
    return { success: false };
  }
}

/**
 * Get current session
 * @returns {Promise<{ data: { session: object | null }, error: object | null }>}
 */
export async function getSession() {
  return await supabase.auth.getSession();
}

/**
 * Get current user role from localStorage (fast) or session (fallback)
 * @returns {Promise<string | null>}
 */
export async function getUserRole() {
  const cachedRole = localStorage.getItem("proofride_role");
  if (cachedRole) {
    return cachedRole;
  }

  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) {
    return null;
  }

  const role = data.session.user?.user_metadata?.role;
  if (role) {
    localStorage.setItem("proofride_role", role);
  }

  return role || null;
}

/**
 * Check if current user has CEO role
 * @returns {Promise<boolean>}
 */
export async function isCeo() {
  const role = await getUserRole();
  return role === "ceo";
}

/**
 * Check if a role has access to a given route role
 * CEO can access everything; others can only access their own role
 * @param {string} userRole - Current user's role
 * @param {string} routeRole - Required role for the route
 * @returns {boolean}
 */
export function hasAccess(userRole, routeRole) {
  if (!routeRole) return true; // public route
  // Two-person lab (post-pivot): any authenticated user has full access to
  // every surface. Route roles are kept only as navigation defaults.
  return Boolean(userRole);
}

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  // Resilient by design: this gate runs before ANY page renders (main.js boot
  // and every router navigation). If the auth backend is slow, paused, or
  // unreachable, fall through to the guest experience — a dead backend must
  // never blank the public landing page.
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise((resolve) =>
        setTimeout(
          () =>
            resolve({
              data: { session: null },
              error: new Error("Auth session check timed out after 4s"),
            }),
          4000,
        ),
      ),
    ]);
    return !result.error && !!result.data?.session;
  } catch (err) {
    console.error(
      "isAuthenticated failed — treating visitor as logged out:",
      err,
    );
    return false;
  }
}

/**
 * Handle session expiry (called on auth state change)
 * @param {function} onExpired - callback when session expires
 */
export function onSessionExpired(onExpired) {
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === "SIGNED_OUT" || (event === "TOKEN_REFRESHED" && !session)) {
      localStorage.removeItem("proofride_role");
      onExpired();
    }
  });
}
